import AttachmentItemsSort from 'owa-service/lib/contract/AttachmentItemsSort';
import AttachmentItemsSortColumn from 'owa-service/lib/contract/AttachmentItemsSortColumn';
import AttachmentItemsSortOrder from 'owa-service/lib/contract/AttachmentItemsSortOrder';
import FileProviderItemViewState from '../../store/schema/FileProviderItemViewState';
import strings from 'owa-strings/owa-mail-strings';
import {
    sortSharedWithMeByDateShared,
    sortSharedWithMeByName,
    sortSharedWithMeBySharedBy,
} from '../getOrSearchFileProviderItems/sortSharedWithMeClientSide';

export const ICON_PROPERTY_NAME: string = 'icon';
export const NAME_PROPERTY_NAME: string = 'name';
export const FROM_PROPERTY_NAME: string = 'from';
export const SUBJECT_PROPERTY_NAME: string = 'subject';
export const LOCATION_ICON_PROPERTY_NAME: string = 'locationIcon';
export const PREVIEW_PROPERTY_NAME: string = 'preview';

const MODIFIED_PROPERTY_NAME: string = 'modifiedTime';
const SIZE_PROPERTY_NAME: string = 'size';
const MODIFIED_BY_PROPERTY_NAME: string = 'modifiedBy';
const DATE_PROPERTY_NAME: string = 'receivedDate';
const SHARED_TIME_PROPERTY_NAME: string = 'sharedTime';
const SHARED_BY_PROPERTY_NAME: string = 'sharedBy';
const DEFAULT_COLUMN_MIN_WIDTH = 96;
const DEFAULT_COLUMN_MAX_WIDTH = 128;
const NAME_COLUMN_MIN_WIDTH = 212;
const NAME_COLUMN_MAX_WIDTH = 432;
const ICON_COLUMN_WIDTH = 20;
const MAIL_SENDER_COLUMN_WIDTH = 190;
const MAIL_SUBJECT_COLUMN_WIDTH = 164;
const MAIL_PREVIEW_MIN_COLUMN_WIDTH = 352;
const MAIL_PREVIEW_MAX_COLUMN_WIDTH = 700;

export const defaultSort: AttachmentItemsSort = Object.freeze({
    SortColumn: AttachmentItemsSortColumn.LastModified,
    SortOrder: AttachmentItemsSortOrder.Descending,
});

export interface FilePickerListColumnInfo {
    name: string;
    minWidth: number;
    maxWidth: number;
    sort: AttachmentItemsSortColumn | null; // null is for columns that cannot be sorted
    clientSort?: (
        items: FileProviderItemViewState[],
        sortAsc: boolean
    ) => FileProviderItemViewState[]; // TODO: 29926 remove this once ODSP adds sort support to Vroom
}

function CreateFilePickerListColumnInfo(
    name: string,
    minWidth: number,
    maxWidth: number,
    sort: AttachmentItemsSortColumn,
    clientSort?: (
        items: FileProviderItemViewState[],
        sortAsc: boolean
    ) => FileProviderItemViewState[]
) {
    return <FilePickerListColumnInfo>{
        name: name,
        minWidth: minWidth,
        maxWidth: maxWidth,
        sort: sort,
        clientSort: clientSort,
    };
}

const filePickerListColumnMapping: {
    [key: string]: () => FilePickerListColumnInfo;
} = Object.freeze({
    [ICON_PROPERTY_NAME]: () =>
        CreateFilePickerListColumnInfo('', ICON_COLUMN_WIDTH, ICON_COLUMN_WIDTH, null),
    [NAME_PROPERTY_NAME]: () =>
        CreateFilePickerListColumnInfo(
            strings.NameColumnHeaderText,
            NAME_COLUMN_MIN_WIDTH,
            NAME_COLUMN_MAX_WIDTH,
            AttachmentItemsSortColumn.Name
        ),
    [MODIFIED_PROPERTY_NAME]: () =>
        CreateFilePickerListColumnInfo(
            strings.ModifiedColumnHeaderText,
            DEFAULT_COLUMN_MIN_WIDTH,
            DEFAULT_COLUMN_MAX_WIDTH,
            AttachmentItemsSortColumn.LastModified
        ),
    [SIZE_PROPERTY_NAME]: () =>
        CreateFilePickerListColumnInfo(
            strings.SizeColumnHeaderText,
            DEFAULT_COLUMN_MIN_WIDTH,
            DEFAULT_COLUMN_MAX_WIDTH,
            AttachmentItemsSortColumn.Size
        ),
    [MODIFIED_BY_PROPERTY_NAME]: () =>
        CreateFilePickerListColumnInfo(
            strings.ModifiedByColumnHeaderText,
            DEFAULT_COLUMN_MIN_WIDTH,
            DEFAULT_COLUMN_MAX_WIDTH,
            null
        ),
    [FROM_PROPERTY_NAME]: () =>
        CreateFilePickerListColumnInfo(
            strings.FromColumnHeaderText,
            DEFAULT_COLUMN_MIN_WIDTH,
            DEFAULT_COLUMN_MAX_WIDTH,
            AttachmentItemsSortColumn.From
        ),
    [SUBJECT_PROPERTY_NAME]: () =>
        CreateFilePickerListColumnInfo(
            strings.SubjectColumnHeaderText,
            DEFAULT_COLUMN_MIN_WIDTH,
            DEFAULT_COLUMN_MAX_WIDTH,
            AttachmentItemsSortColumn.Subject
        ),
    [DATE_PROPERTY_NAME]: () =>
        CreateFilePickerListColumnInfo(
            strings.DateColumnHeaderText,
            DEFAULT_COLUMN_MIN_WIDTH,
            DEFAULT_COLUMN_MAX_WIDTH,
            AttachmentItemsSortColumn.ReceivedDate
        ),
    [LOCATION_ICON_PROPERTY_NAME]: () =>
        CreateFilePickerListColumnInfo(
            strings.LocationColumnHeaderText,
            DEFAULT_COLUMN_MIN_WIDTH,
            DEFAULT_COLUMN_MIN_WIDTH,
            null
        ),
});

// We don't support sort by size in ODC since it mixes folders with files, and the sort looks incorrect.
const oneDriveConsumerFilePickerListColumnMapping: {
    [key: string]: () => FilePickerListColumnInfo;
} = Object.freeze({
    [SIZE_PROPERTY_NAME]: () =>
        CreateFilePickerListColumnInfo(
            strings.SizeColumnHeaderText,
            DEFAULT_COLUMN_MIN_WIDTH,
            DEFAULT_COLUMN_MAX_WIDTH,
            null
        ),
});

// We don't support sort by name in ODB SharedWithMe since ODSP does not have an API that supports it
// We do suport sort by modified by for SharedWithMe, as it is supported, and useful for this scope
const oneDriveProSharedWithMeFilePickerListColumnMapping: {
    [key: string]: () => FilePickerListColumnInfo;
} = Object.freeze({
    [NAME_PROPERTY_NAME]: () =>
        CreateFilePickerListColumnInfo(
            strings.NameColumnHeaderText,
            NAME_COLUMN_MIN_WIDTH,
            NAME_COLUMN_MAX_WIDTH,
            AttachmentItemsSortColumn.Name,
            sortSharedWithMeByName
        ),
    [MODIFIED_BY_PROPERTY_NAME]: () =>
        CreateFilePickerListColumnInfo(
            strings.ModifiedByColumnHeaderText,
            DEFAULT_COLUMN_MIN_WIDTH,
            DEFAULT_COLUMN_MAX_WIDTH,
            AttachmentItemsSortColumn.ModifiedBy
        ),
    [SHARED_TIME_PROPERTY_NAME]: () =>
        CreateFilePickerListColumnInfo(
            strings.SharedColumnHeaderText,
            DEFAULT_COLUMN_MIN_WIDTH,
            DEFAULT_COLUMN_MAX_WIDTH,
            AttachmentItemsSortColumn.SharedTime,
            sortSharedWithMeByDateShared
        ),
    [SHARED_BY_PROPERTY_NAME]: () =>
        CreateFilePickerListColumnInfo(
            strings.SharedByColumnHeaderText,
            DEFAULT_COLUMN_MIN_WIDTH,
            DEFAULT_COLUMN_MAX_WIDTH,
            AttachmentItemsSortColumn.SharedBy,
            sortSharedWithMeBySharedBy
        ),
});

const mailMessageListColumnMapping: {
    [key: string]: () => FilePickerListColumnInfo;
} = Object.freeze({
    [FROM_PROPERTY_NAME]: () =>
        CreateFilePickerListColumnInfo(
            strings.SenderColumnHeaderText,
            MAIL_SENDER_COLUMN_WIDTH,
            MAIL_SENDER_COLUMN_WIDTH,
            null
        ),
    [SUBJECT_PROPERTY_NAME]: () =>
        CreateFilePickerListColumnInfo(
            strings.SubjectColumnHeaderText,
            MAIL_SUBJECT_COLUMN_WIDTH,
            MAIL_SUBJECT_COLUMN_WIDTH,
            null
        ),
    [PREVIEW_PROPERTY_NAME]: () =>
        CreateFilePickerListColumnInfo(
            strings.MessagePreviewColumnHeaderText,
            MAIL_PREVIEW_MIN_COLUMN_WIDTH,
            MAIL_PREVIEW_MAX_COLUMN_WIDTH,
            null
        ),
    [DATE_PROPERTY_NAME]: () =>
        CreateFilePickerListColumnInfo(
            strings.DateColumnHeaderText,
            DEFAULT_COLUMN_MIN_WIDTH,
            DEFAULT_COLUMN_MAX_WIDTH,
            null
        ),
});

export default function getDefaultFilePickerListColumnInfo(name: string): FilePickerListColumnInfo {
    if (name in filePickerListColumnMapping) {
        return filePickerListColumnMapping[name]();
    }
    return <FilePickerListColumnInfo>{
        name: name,
        minWidth: DEFAULT_COLUMN_MIN_WIDTH,
        maxWidth: DEFAULT_COLUMN_MAX_WIDTH,
        sort: null,
    };
}

export function getODCFilePickerListColumnInfo(name: string): FilePickerListColumnInfo {
    if (name in oneDriveConsumerFilePickerListColumnMapping) {
        return oneDriveConsumerFilePickerListColumnMapping[name]();
    }

    return getDefaultFilePickerListColumnInfo(name);
}

export function getODBFilePickerListColumnInfo(
    name: string,
    isSharedWithMe: boolean = false
): FilePickerListColumnInfo {
    if (isSharedWithMe && name in oneDriveProSharedWithMeFilePickerListColumnMapping) {
        return oneDriveProSharedWithMeFilePickerListColumnMapping[name]();
    }

    return getDefaultFilePickerListColumnInfo(name);
}

export function getMailMessageListColumnInfo(name: string): FilePickerListColumnInfo {
    if (name in mailMessageListColumnMapping) {
        return mailMessageListColumnMapping[name]();
    }

    return getDefaultFilePickerListColumnInfo(name);
}
